// src/components/kyc/ApplicantCorporateEntityShareHoldersStep.tsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import GlobalTextInputField from "../../components/GlobalTextInputField";
import UploadTableSingle from "./UploadTableSingle";
import Info from "../../assets/info.svg";

import type {
    CorporateShareholder,
    StepCorporateShareholdersState
} from "../../types/types";
import { StepperNavContext } from "../../components/Stepper";
import UploadTable from "./UploadTable";

type Props = {
    value: StepCorporateShareholdersState;
    onChange: (patch: Partial<StepCorporateShareholdersState>) => void;
    readOnly?: boolean;
    isAdmin?: boolean;
};

const emptyCorporate: CorporateShareholder = {
    companyName: "",
    companyRegNo: "",
    countryOfIncorporation: "",
    dateOfIncorporation: "",
    legalAddress: "",
    businessAddress: "",
    percentageOwnership: "",
    appointedRepName: "",
    positionTitle: "",
    dateOfBirth: "",
    nationality: "",
    passportNo: "",
    address: "",
    shareholdersOver20ListUrl: "",
    additionalUrls: []
};

const Label: React.FC<React.PropsWithChildren> = ({ children }) => (
    <label className="text-[10px] font-semibold text-text-base">{children}</label>
);

const ordinal = (n: number) => {
    const v = n % 100;
    if (v >= 11 && v <= 13) return `${n}th`;
    switch (n % 10) {
        case 1: return `${n}st`;
        case 2: return `${n}nd`;
        case 3: return `${n}rd`;
        default: return `${n}th`;
    }
};

const ApplicantCorporateEntityShareHoldersStep: React.FC<Props> = ({
    value,
    onChange,
    readOnly,
    isAdmin
}) => {
    const { goNext, loading } = useContext(StepperNavContext);

    const list = useMemo(
        () =>
            value.corporateShareholders?.length
                ? value.corporateShareholders
                : [{ ...emptyCorporate }],
        [value.corporateShareholders]
    );

    useEffect(() => {
        if (!value.corporateShareholders?.length)
            onChange({ corporateShareholders: list });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [active, setActive] = useState(0);

    useEffect(() => {
        if (active > list.length - 1)
            setActive(Math.max(0, list.length - 1));
    }, [list.length, active]);

    const setList = (next: CorporateShareholder[]) =>
        onChange({ corporateShareholders: next });

    const updateAt = (idx: number, patch: Partial<CorporateShareholder>) => {
        const next = list.map((row, i) =>
            i === idx ? { ...row, ...patch } : row
        );
        setList(next);
    };

    const addNew = () => {
        setList([...list, { ...emptyCorporate }]);
        setActive(list.length);
    };

    const removeAt = (idx: number) => {
        if (list.length === 1) {
            setList([{ ...emptyCorporate }]);
            setActive(0);
            return;
        }
        const next = list.filter((_, i) => i !== idx);
        setList(next);
        if (active >= next.length) setActive(next.length - 1);
    };

    const c = list[active];

    const handleNoCorpChange = (checked: boolean) => {
        onChange({ noCorporateShareholders: checked });
    };

    const handleSkip = () => {
        if (value.noCorporateShareholders) goNext();
    };

    return (
        <div className="space-y-4">
            <p className="text-[10px] text-text-base">
                Provide details of any corporate entities that directly own 20% or more
                of the applicant entity. This includes incorporation information, legal
                address, and details of the appointed representative authorized to act
                on behalf of the corporate shareholder.
            </p>

            {/* Checkbox and skip */}
            <div className="flex gap-3 mt-1">
                <label className="inline-flex items-center gap-2 text-[10px] cursor-pointer">
                    <input
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer"
                        checked={!!value.noCorporateShareholders}
                        onChange={(e) => handleNoCorpChange(e.target.checked)}
                        disabled={readOnly}
                    />
                    <span>We don’t have corporate shareholders</span>
                </label>

                {!readOnly && (
                    <button
                        type="button"
                        disabled={!value.noCorporateShareholders || loading}
                        onClick={handleSkip}
                        className={`text-[12px] px-2 py-1 rounded-md min-w-20 border ${value.noCorporateShareholders && !loading
                            ? "border-text-button-tertiary text-text-button-tertiary hover:bg-black/5"
                            : "border-border-disabled text-text-disabled cursor-not-allowed"
                            }`}
                    >
                        Skip
                    </button>
                )}
            </div>

            {/* Form area */}
            <div
                className={`rounded-lg border border-border-subtle p-4 space-y-4 ${value.noCorporateShareholders
                    ? "opacity-60 pointer-events-none"
                    : ""
                    }`}
            >
                <div className="flex items-center justify-between">
                    <h3 className="text-[14px] font-semibold">
                        Corporate Shareholder {ordinal(active + 1)}
                    </h3>
                    {!readOnly && (
                        <button
                            type="button"
                            onClick={() => removeAt(active)}
                            className="text-[12px] underline disabled:opacity-50"
                            disabled={list.length === 1}
                        >
                            Remove
                        </button>
                    )}
                </div>

                <div className="flex items-center gap-3 overflow-x-auto">
                    {list.map((_, i) => {
                        const isActive = i === active;
                        return (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setActive(i)}
                                className={`rounded-md px-3 py-1.5 text-[14px] font-semibold whitespace-nowrap ${isActive
                                    ? "bg-[#E6F0FA] text-text-base"
                                    : "bg-transparent text-text-base/80 hover:bg-black/5"
                                    }`}
                                disabled={readOnly}
                            >
                                {ordinal(i + 1)}
                            </button>
                        );
                    })}
                    {!readOnly && (
                        <button
                            type="button"
                            onClick={addNew}
                            className="text-[14px] font-semibold whitespace-nowrap hover:underline"
                        >
                            + Add a new Corporate Shareholder
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <GlobalTextInputField
                        title="COMPANY NAME"
                        value={c.companyName}
                        onChange={(e: any) =>
                            updateAt(active, { companyName: e.target.value })
                        }
                        placeholder="Enter company name"
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="COMPANY REG. NO."
                        value={c.companyRegNo}
                        onChange={(e: any) =>
                            updateAt(active, { companyRegNo: e.target.value })
                        }
                        placeholder="Enter company registration number"
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="COUNTRY OF INCORPORATION"
                        value={c.countryOfIncorporation}
                        onChange={(e: any) =>
                            updateAt(active, {
                                countryOfIncorporation: e.target.value
                            })
                        }
                        placeholder="Enter country"
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="DATE OF INCORPORATION"
                        type="date"
                        value={c.dateOfIncorporation}
                        onChange={(e: any) =>
                            updateAt(active, {
                                dateOfIncorporation: e.target.value
                            })
                        }
                        showBorder
                        disabled={readOnly}
                    />

                    <div className="col-span-2">
                        <GlobalTextInputField
                            title="LEGAL ADDRESS"
                            value={c.legalAddress}
                            onChange={(e: any) =>
                                updateAt(active, { legalAddress: e.target.value })
                            }
                            placeholder="Enter legal address"
                            showBorder
                            disabled={readOnly}
                        />
                    </div>

                    <div className="col-span-2">
                        <GlobalTextInputField
                            title="BUSINESS ADDRESS (IF DIFFERENT)"
                            value={c.businessAddress}
                            onChange={(e: any) =>
                                updateAt(active, {
                                    businessAddress: e.target.value
                                })
                            }
                            placeholder="Enter business address"
                            showBorder
                            disabled={readOnly}
                        />
                    </div>

                    <GlobalTextInputField
                        title="PERCENTAGE OF OWNERSHIP"
                        type="number"
                        value={c.percentageOwnership}
                        onChange={(e: any) => {
                            const v = e.target.value;
                            if (Number(v) < 0) return;
                            updateAt(active, { percentageOwnership: v });
                        }}
                        placeholder="e.g. 40"
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="APPOINTED REPRESENTATIVE NAME"
                        value={c.appointedRepName}
                        onChange={(e: any) =>
                            updateAt(active, {
                                appointedRepName: e.target.value
                            })
                        }
                        placeholder="Enter representative's full name"
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="POSITION / TITLE"
                        value={c.positionTitle}
                        onChange={(e: any) =>
                            updateAt(active, { positionTitle: e.target.value })
                        }
                        placeholder="e.g. Director, Authorized Signatory"
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="DATE OF BIRTH"
                        type="date"
                        value={c.dateOfBirth}
                        onChange={(e: any) =>
                            updateAt(active, { dateOfBirth: e.target.value })
                        }
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="NATIONALITY"
                        value={c.nationality}
                        onChange={(e: any) =>
                            updateAt(active, { nationality: e.target.value })
                        }
                        placeholder="Enter nationality"
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="PASSPORT NO."
                        value={c.passportNo}
                        onChange={(e: any) =>
                            updateAt(active, { passportNo: e.target.value })
                        }
                        placeholder="Enter passport number"
                        showBorder
                        disabled={readOnly}
                    />

                    <div className="col-span-2">
                        <GlobalTextInputField
                            title="ADDRESS"
                            value={c.address}
                            onChange={(e: any) =>
                                updateAt(active, { address: e.target.value })
                            }
                            placeholder="Enter address"
                            showBorder
                            disabled={readOnly}
                        />
                    </div>

                    {/* SINGLE UPLOAD (REPLACED) */}
                    {/* DOCUMENTS ROW */}
                    <div className="col-span-2 grid grid-cols-2 gap-4">
                        {/* SHAREHOLDERS >20% */}
                        <div className="flex flex-col gap-1 text-[10px]">
                            <Label>
                                DOCUMENTS TO BE SUBMITTED — List of Shareholders Owning &gt;20%
                            </Label>
                            <div className="flex items-center gap-1">
                                <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                    <img src={Info} alt="" className="w-3 h-3" />
                                </div>
                                <span className="text-[8px]">
                                    Upload a document listing all shareholders owning more than 20%
                                    of the corporate entity
                                </span>
                            </div>
                            <UploadTableSingle
                                value={c.shareholdersOver20ListUrl}
                                onChange={(url) =>
                                    updateAt(active, { shareholdersOver20ListUrl: url })
                                }
                                readOnly={readOnly}
                            />
                        </div>

                        {/* ADDITIONAL DOCUMENTS */}
                        <div className="flex flex-col gap-1 text-[10px]">
                            <Label>ADDITIONAL DOCUMENTS</Label>
                            <div className="flex items-center gap-1">
                                <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                    <img src={Info} alt="" className="w-3 h-3" />
                                </div>
                                <span className="text-[8px]">
                                    Upload any additional documents relevant to your application
                                </span>
                            </div>
                            <UploadTable
                                values={c.additionalUrls ?? []}
                                onChange={(i, url) =>
                                    updateAt(active, {
                                        additionalUrls: (c.additionalUrls ?? []).map((u, idx) =>
                                            idx === i ? url : u
                                        ),
                                    })
                                }
                                add={(url) =>
                                    updateAt(active, {
                                        additionalUrls: [...(c.additionalUrls ?? []), url],
                                    })
                                }
                                remove={(i) =>
                                    updateAt(active, {
                                        additionalUrls: (c.additionalUrls ?? []).filter(
                                            (_, idx) => idx !== i
                                        ),
                                    })
                                }
                                readOnly={readOnly}
                            />
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ApplicantCorporateEntityShareHoldersStep;
